<template>
	<div class="kt-field-select">
		<div
			ref="tippyRef"
			@mouseenter="isFieldHovered = true"
			@mouseleave="isFieldHovered = false"
		>
			<KtField
				v-bind="{ field }"
				ref="ktFieldRef"
				:getEmptyValue="() => (isMultiple ? [] : null)"
				:helpTextSlot="helpTextSlot"
			>
				<div :class="inputWrapperClasses">
					<slot
						:currentValue="
							isMultiple ? visibleSelectedTags : field.currentValue
						"
						name="selection"
						:removeValue="removeTag"
					>
						<KtTag
							v-for="(option, index) in visibleSelectedTags"
							:key="index"
							:isDisabled="field.isDisabled || Boolean(option.isDisabled)"
							:text="option.label"
							@close="removeTag(option.value)"
						/>
						<KtTag
							v-if="collapsedTagCount > 0"
							isDisabled
							:text="`+${collapsedTagCount}`"
						/>
					</slot>
					<input
						ref="inputRef"
						v-bind="inputProps"
						@blur="isFieldFocused = false"
						@focus="isFieldFocused = true"
						@input="updateQuery"
						@keydown.delete="onPressDelete"
						@keydown.down.prevent
						@keydown.enter.prevent
						@keydown.up.prevent
					/>
				</div>
				<template #actionIcon="{ classes, handleClear, hasClear, showClear }">
					<ActionIcon
						v-bind="{
							classes,
							handleClear,
							hasClear,
							isDropdownOpen,
							showClear: showClearIcon(showClear),
						}"
						@update:isDropdownOpen="setIsDropdownOpen"
					/>
				</template>
			</KtField>
		</div>
		<div ref="tippyContentRef">
			<FieldSelectOptions
				:actions="actions"
				:dataTestPrefix="inputProps['data-test']"
				:groups="groups"
				:isDisabled="field.isDisabled"
				:isDropdownOpen="isDropdownOpen"
				:isLoading="isLoadingOptions"
				:isMultiple="isMultiple"
				:isUnsorted="isUnsorted"
				:maximumSelectable="maximumSelectable"
				:modelValue="optionsValue"
				:options="filteredOptions"
				@close="setIsDropdownOpen(false)"
				@update:modelValue="onOptionsInput"
			>
				<template #option="values">
					<slot v-bind="values" name="option" />
				</template>
			</FieldSelectOptions>
		</div>
	</div>
</template>

<script lang="ts">
import type { PropType, Slot } from 'vue'
import { computed, defineComponent, ref, watch } from 'vue'
import { z } from 'zod'

import { Yoco } from '@3yourmind/yoco'

import { KtField } from '../../kotti-field'
import { useField } from '../../kotti-field/hooks'
import {
	useEmitBlur,
	useForceUpdate,
	useKtFieldRef,
} from '../../kotti-field/hooks'
import { KtTag } from '../../kotti-tag'
import { makeProps } from '../../make-props'
import { KOTTI_FIELD_SELECT_SUPPORTS } from '../constants'
import {
	isTippyContentWrapper,
	useSelectTippy,
} from '../hooks/use-select-tippy'
import type {
	KottiFieldMultiSelect,
	KottiFieldMultiSelectRemote,
	KottiFieldSingleSelect,
	KottiFieldSingleSelectRemote,
} from '../types'
import { Shared } from '../types'

import ActionIcon from './ActionIcon.vue'
import FieldSelectOptions from './Options.vue'

const UPDATE_QUERY = 'update:query'

const propsSchema = Shared.propsSchema
	.merge(Shared.isMultipleSchema)
	.merge(Shared.isRemoteSchema)
	.merge(Shared.isSingleSchema)
	.omit({ modelValue: true })
	.extend({
		helpTextSlot: z.function().returns(z.array(z.any())).optional(),
		isMultiple: z.boolean().default(false),
		isRemote: z.boolean().default(false),
		modelValue: z.union([
			Shared.isMultipleSchema.shape.modelValue,
			Shared.isSingleSchema.shape.modelValue,
		]),
	})

type MultiValue =
	| KottiFieldMultiSelect.ModelValue
	| KottiFieldMultiSelectRemote.ModelValue

type SingleValue =
	| KottiFieldSingleSelect.ModelValue
	| KottiFieldSingleSelectRemote.ModelValue

export default defineComponent({
	name: 'GenericSelectField',
	components: {
		ActionIcon,
		FieldSelectOptions,
		KtField,
		KtTag,
	},
	props: {
		...makeProps(propsSchema),
		helpTextSlot: {
			default: undefined,
			type: Function as PropType<Slot<undefined>>,
		},
	},
	emits: ['blur', 'emit', 'open', 'update:modelValue', UPDATE_QUERY],
	setup(props, { emit: rawEmit }) {
		const emit = (event: string, payload: unknown) => {
			rawEmit('emit', { event, payload })
		}

		const field = useField<MultiValue | SingleValue>({
			emit,
			isEmpty: (value) =>
				props.isMultiple
					? (value as MultiValue).length === 0
					: (value as SingleValue) === null,
			props,
			supports: KOTTI_FIELD_SELECT_SUPPORTS,
		})

		const inputRef = ref<HTMLInputElement | null>(null)
		const isFieldFocused = ref(false)
		const isFieldHovered = ref(false)
		const localQuery = ref<string | null>(null)

		const { forceUpdateKey, forceUpdate } = useForceUpdate()
		const ktFieldRef = useKtFieldRef()

		const triggerTargets = computed(() =>
			ktFieldRef.value ? [ktFieldRef.value.inputContainerRef] : [],
		)
		const dropdownStyles = computed(() => props.dropdownStyles ?? {})

		const { isDropdownOpen, isDropdownMounted, ...selectTippy } =
			useSelectTippy(field, triggerTargets, dropdownStyles)

		const deleteQuery = () => {
			if (props.isRemote) {
				if (props.query !== null) emit(UPDATE_QUERY, null)
			} else localQuery.value = null
		}

		/**
		 * Tippy wraps the content inside additional div elements.
		 * So, we need to look for the actual content element.
		 */
		const findEventTarget = (
			target: EventTarget | null,
		): EventTarget | null => {
			if (target === null || !(target instanceof HTMLElement)) return target

			if (target === selectTippy.tippyContentRef.value) return target

			return isTippyContentWrapper(target)
				? findEventTarget((target.childNodes[0] ?? null) as EventTarget | null)
				: target
		}
		useEmitBlur({
			emit,
			field,
			fieldTarget: computed(() => [
				ktFieldRef.value?.inputContainerRef ?? null,
				selectTippy.tippyContentRef.value,
			]),
			findEventTarget,
		})

		watch(
			isDropdownMounted,
			(isMounted) => {
				if (isMounted) return
				deleteQuery()
			},
			{ flush: 'post' },
		)

		watch(
			isDropdownOpen,
			(isOpen) => {
				if (!isOpen) return
				inputRef.value?.focus()
				emit('open', undefined)
			},
			{ flush: 'post' },
		)

		const queryValue = computed(() =>
			props.isRemote ? props.query : localQuery.value,
		)

		/**
		 * keeps reference of already seen option labels so that
		 * the tags can be rendered properly when filtering remotely
		 */
		const seenValueLabelMap = ref<Map<Shared.Value, Shared.Option['label']>>(
			new Map(),
		)

		watch(
			() => props.options,
			() => {
				seenValueLabelMap.value = new Map([
					...seenValueLabelMap.value,
					...props.options.map(
						(option): [Shared.Value, Shared.Option['label']] => [
							option.value,
							option.label,
						],
					),
				])
			},
			{ flush: 'post', immediate: true },
		)

		/**
		 * Make sure the input field does not take up the last row on its own
		 * when tags are shown and the dropwown is closed.
		 */
		const isInputVisible = computed(
			() => !props.isMultiple || field.isEmpty || isDropdownOpen.value,
		)

		return {
			collapsedTagCount: computed(() =>
				props.isMultiple
					? (field.currentValue as MultiValue).length - props.collapseTagsAfter
					: 0,
			),
			field,
			filteredOptions: computed(() =>
				props.isRemote
					? props.options
					: props.options.filter(
							(o) =>
								localQuery.value === null ||
								o.label.toLowerCase().includes(localQuery.value.toLowerCase()),
						),
			),
			inputProps: computed(() => ({
				...field.inputProps,
				autocomplete: props.autoComplete,
				class: ['kt-field-select__input-field'],
				forceUpdateKey: forceUpdateKey.value,
				placeholder: props.placeholder ?? undefined,
				size: 1,
				type: 'text',
				value: (() => {
					if (isDropdownOpen.value) return queryValue.value ?? undefined

					// can't bind multiple values to input (therefore, we just bind `undefined`)
					if (field.currentValue === null || props.isMultiple) return undefined

					return props.options.find(
						(option) => option.value === field.currentValue,
					)?.label
				})(),
			})),
			inputRef,
			inputWrapperClasses: computed(() => ({
				'kt-field-select__input-wrapper': true,
				'kt-field-select__input-wrapper--has-tags': props.isMultiple,
				'kt-field-select__input-wrapper--hide-input-field':
					!isInputVisible.value,
			})),
			isDropdownOpen,
			isFieldFocused,
			isFieldHovered,
			ktFieldRef,
			onOptionsInput: (value: MultiValue) => {
				if (props.isMultiple) {
					field.setValue(value)
					if (props.clearOnSelect) {
						deleteQuery()
						inputRef.value?.focus()
					}
				} else {
					const newValue = value[0] ?? null
					// performance optimization
					if (field.currentValue !== newValue) field.setValue(newValue)
				}
				// single select: close the tippy instance whenever a selection is made.
				// This (watcher on isDropdownMounted) also intentionally resets the query
				// so that the api-call (for example) can already trigger and load the non-filtered options
				if (!props.isMultiple) selectTippy.setIsDropdownOpen(false)
			},
			onPressDelete: () => {
				if (queryValue.value || !props.isMultiple) return // only delete value if query is already empty

				const value = field.currentValue as MultiValue
				if (value.length > 0) field.setValue(value.slice(0, -1))
			},
			optionsValue: computed(() => {
				if (props.isMultiple) return field.currentValue as MultiValue
				else
					return field.currentValue === null
						? []
						: [field.currentValue as SingleValue]
			}),
			removeTag: (value: Shared.Option['value']) => {
				if (!props.isMultiple)
					throw new Error(
						'GenericSelectField: Unexpected multi function on single select',
					)

				field.setValue(
					(field.currentValue as MultiValue).filter((v) => v !== value),
				)
				inputRef.value?.focus()
			},
			setIsDropdownOpen: selectTippy.setIsDropdownOpen,
			showClearIcon: computed(
				() => (showClear: boolean) =>
					showClear && (isFieldHovered.value || isFieldFocused.value),
			),
			tippyContentRef: selectTippy.tippyContentRef,
			tippyRef: selectTippy.tippyRef,
			updateQuery: (event: Event) => {
				if (!isDropdownOpen.value) {
					selectTippy.setIsDropdownOpen(true)
				} else {
					const value = (event.target as HTMLInputElement).value
					const newValue = value === '' ? null : value

					if (props.isRemote && props.query !== newValue)
						emit(UPDATE_QUERY, newValue)
					else localQuery.value = newValue
				}

				forceUpdate()
			},
			visibleSelectedTags: computed(() =>
				props.isMultiple
					? (field.currentValue as MultiValue)
							.filter((_, index) => index < props.collapseTagsAfter)
							.map(
								(value): Shared.Option =>
									props.options.find((option) => option.value === value) ?? {
										label: seenValueLabelMap.value.get(value) ?? String(value),
										value,
									},
							)
					: [],
			),
			Yoco,
		}
	},
})
</script>

<style lang="scss" scoped>
.kt-field-select {
	&:not(:last-child) {
		margin-bottom: 0.8rem;
	}

	&__query {
		display: flex;
		flex: 1;
		width: 100%;
		padding: 0;
		margin: 0;
		line-height: 1.6;
		border: 0;
	}

	$vertical-tag-gap: 2px;
	$horizontal-tag-gap: 4px;

	&__input-wrapper {
		display: flex;
		align-items: center;
		cursor: text;

		&--has-tags {
			flex-wrap: wrap;

			// HACK: use negative margins to align multi-line grids of tags
			margin: #{-$vertical-tag-gap + 4px} #{-$horizontal-tag-gap};
		}

		&--hide-input-field {
			position: relative;

			.kt-field-select__input-field {
				position: absolute;
				inset: 0;
				z-index: -1;
			}
		}
	}

	&__input-field {
		display: flex;
		flex: 1 1 auto;
		min-width: 25%;
		padding: 0;
		margin: 0;
		line-height: 1.6;
		border: 0;
	}
}
</style>
